import { useEffect, useState } from "react";
import { useStaticAppHeight } from "./useStaticAppHeight";

/** How long to keep re-measuring after a focus change or a viewport change.
    WebKit's keyboard animation runs about 250ms and reports its side effects
    late, so the measurements below have to be sampled per frame for a while
    rather than on events alone. Same window as useAppViewport's own pump. */
const KEYBOARD_SETTLE_MS = 600;

/** How long to wait for the sheet to grant the scrollable space the focused
    field needs before scrolling anyway. The space is the sheet's own spacer,
    sized from the height this hook returns, so it lands a React commit or two
    after the measurement — see scrollWhenRoomExists. */
const SCROLL_ROOM_WAIT_MS = 300;

/** How long to let a smooth scroll land before finishing it instantly — see
    commitScroll. Longer than the ~250ms WebKit takes over one, short enough
    that a dropped scroll is corrected while the keyboard is still animating. */
const SCROLL_VERIFY_MS = 500;

/** Measures how much of a scrolling surface a software keyboard is covering,
    and keeps the focused field visible above it. Two consumers today: the
    lab-upload sheet and the questionnaire's section screen, both a surface
    with exactly one scroll container inside it, which is all the mechanics
    below assume.

    Pins --app-height so the surface stays perfectly still when the keyboard
    appears, and scrolls the focused field into view inside it. Nothing
    already on screen moves to get out of the keyboard's way; the keyboard is
    an overlay and is allowed to cover what it covers — same posture as
    onboarding.

    What to do about the covered space is the caller's own business: the sheet
    hands the returned height to SheetContent's `keyboardHeight`, which adds it
    as scrollable space and freezes the sheet's height while it is non-zero;
    the section screen renders a spacer of that height inside its scroll
    container, which comes to the same thing.
    Writing padding onto the sheet from out here instead grew it into its
    `max-height` cap, which moved the very top edge this hook exists to keep
    still.

    The pin has to hold growth as well, which onboarding's freeze does not.
    The sheet is anchored to the *bottom* of #root, so it only stays put if
    #root's bottom edge does, and in a mobile browser that edge moves: Safari
    retracts its own bottom toolbar as focus lands and hands ~130px back as
    viewport growth, which the default freeze lets through. That is what was
    dragging the whole sheet down under the keyboard. Correcting it afterwards
    was tried twice and does not work — a transform can't be aimed, because
    WebKit's reporting of the pan (`visualViewport.offsetTop`) disagrees with
    where it actually paints a fixed element, and growing the sheet to full
    height only corrects the drop after it has already been painted, which
    reads as a jump. Not letting #root move in the first place leaves nothing
    to correct.

    Call it from a component mounted inside the surface, so the pin and the
    listeners live exactly as long as that surface does. `formRef` is the
    scrolling content's wrapper; its parent is taken to be the scroll
    container. */
export function useKeyboardScrollOffset(
  formRef: { current: HTMLElement | null },
): number {
  useStaticAppHeight({ freezeGrowth: true });
  const [hidden, setHidden] = useState(0);

  useEffect(() => {
    const vp = window.visualViewport;
    const form = formRef.current;
    if (!vp || !form) return;

    const container = form.parentElement as HTMLElement | null;
    if (!container) return;

    const rootEl = document.getElementById("root");
    if (!rootEl) return;

    let verifyFrame: number | null = null;

    /** How much of the sheet the keyboard covers. #root is pinned to its
        pre-keyboard height, so the sheet's bottom edge still sits where the
        visible area used to end, and what the keyboard has taken is the
        difference between that and the visible height now. */
    function getHiddenHeight(): number {
      if (!rootEl || !vp) return 0;
      return Math.max(0, Math.round(rootEl.clientHeight - vp.height));
    }

    function update() {
      setHidden(getHiddenHeight());
    }

    /** Works in client coordinates, which are the visible area's own: #root is
        fixed at top 0 and the document scroll is held there, so what is
        visible runs from the visual viewport's top edge to its bottom one,
        whatever sits above the container. Deriving the container's visible
        span from its own height instead only holds for a surface whose bottom
        edge is #root's -- true of the sheet, false of the section screen,
        whose scroll container ends above a footer and which therefore
        overshot the field and scrolled it up behind the header. */
    function scrollFocusedIntoView() {
      const active = document.activeElement;
      if (!active || !form?.contains(active) || !container || !vp) return;

      const fieldRect = active.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const fieldTop = fieldRect.top;
      const fieldBottom = fieldTop + fieldRect.height;
      const visibleTop = Math.max(containerRect.top, vp.offsetTop);
      const visibleBottom = Math.min(containerRect.top + containerRect.height, vp.offsetTop + vp.height);
      if (visibleBottom <= visibleTop) return;

      const margin = 16;
      let delta = 0;
      if (fieldBottom + margin > visibleBottom) {
        // Never past the field's own top edge: a field taller than the room
        // left over is better cut off at the bottom than lifted out of sight.
        delta = Math.min(fieldBottom + margin - visibleBottom, Math.max(0, fieldTop - margin - visibleTop));
      } else if (fieldTop - margin < visibleTop) {
        delta = fieldTop - margin - visibleTop;
      }
      if (delta !== 0) commitScroll(Math.max(0, container.scrollTop + delta));
    }

    /** Asks for a smooth scroll, then makes sure it actually happened. WebKit
        abandons a programmatic smooth scroll when anything else scrolls the
        same container while it is running, and a keyboard opening over a
        focused field is exactly that — so an unverified request can simply be
        dropped, which looks identical to never having scrolled at all. If it
        hasn't landed by the deadline the scroll is finished by hand, without
        the animation. */
    function commitScroll(target: number) {
      if (!container) return;
      container.scrollTo({ top: target, behavior: "smooth" });

      const deadline = performance.now() + SCROLL_VERIFY_MS;
      const verify = () => {
        verifyFrame = null;
        if (!container) return;
        if (Math.abs(container.scrollTop - target) <= 1) return;
        if (performance.now() >= deadline) {
          container.scrollTop = target;
          return;
        }
        verifyFrame = requestAnimationFrame(verify);
      };
      if (verifyFrame !== null) cancelAnimationFrame(verifyFrame);
      verifyFrame = requestAnimationFrame(verify);
    }

    let frame: number | null = null;
    let pumpUntil = 0;
    let scrollFrame: number | null = null;
    let scrollUntil = 0;

    /** Wait for the sheet to actually be scrollable before scrolling it. The
        room is the sheet's own spacer, sized from the height this hook
        returns — so it exists only after that number has been through React
        and been painted, which is later than the frame the last measurement
        lands on. Scrolling before then is silently clamped to the current
        scrollHeight, i.e. it does nothing at all.

        Any room at all, rather than the full covered height: the sheet's
        resting bottom padding is inside its frozen height, so the space it
        gains is a little short of what the keyboard took, and that
        difference is empty padding nothing needs to scroll past. */
    function scrollWhenRoomExists() {
      scrollFrame = null;
      if (!container) return;
      const room = container.scrollHeight - container.clientHeight;
      if (room > 0 || performance.now() >= scrollUntil) {
        scrollFocusedIntoView();
        return;
      }
      scrollFrame = requestAnimationFrame(scrollWhenRoomExists);
    }

    function tick() {
      frame = null;
      update();
      if (performance.now() < pumpUntil) {
        frame = requestAnimationFrame(tick);
      } else {
        // Once at the end rather than per frame: WebKit reports the
        // keyboard's height in stages, so an earlier scroll would be computed
        // against a value that is about to change, and a smooth scroll
        // restarted every frame never arrives.
        scrollUntil = performance.now() + SCROLL_ROOM_WAIT_MS;
        scrollFrame ??= requestAnimationFrame(scrollWhenRoomExists);
      }
    }

    function pump() {
      update();
      pumpUntil = performance.now() + KEYBOARD_SETTLE_MS;
      frame ??= requestAnimationFrame(tick);
    }

    function handleFocusIn(e: FocusEvent) {
      if (!form?.contains(e.target as Node)) return;
      // Only an element that actually summons a software keyboard -- a
      // choice/boolean/multi-choice row is a <button role="radio"|"checkbox">
      // (see ChoiceRow), and focusing one on tap is not a text field asking
      // for room. Treating it as one was scrolling the answer the client had
      // just picked back into view (and, worse, leaving a spacer sized for a
      // keyboard that was never opening).
      if (!isTextEntryElement(e.target)) return;
      pump();
    }

    function handleFocusOut(e: FocusEvent) {
      if (!isTextEntryElement(e.target)) return;
      pump();
    }

    vp.addEventListener("resize", pump);
    vp.addEventListener("scroll", pump);
    form.addEventListener("focusin", handleFocusIn);
    form.addEventListener("focusout", handleFocusOut);

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      if (scrollFrame !== null) cancelAnimationFrame(scrollFrame);
      if (verifyFrame !== null) cancelAnimationFrame(verifyFrame);
      vp.removeEventListener("resize", pump);
      vp.removeEventListener("scroll", pump);
      form.removeEventListener("focusin", handleFocusIn);
      form.removeEventListener("focusout", handleFocusOut);
    };
  }, [formRef]);

  return hidden;
}

const TEXT_ENTRY_INPUT_TYPES = new Set(["text", "number", "email", "tel", "search", "url", "password"]);

function isTextEntryElement(target: EventTarget | null): boolean {
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) return TEXT_ENTRY_INPUT_TYPES.has(target.type);
  return false;
}
